import { useEffect, useState } from 'react';

import { ExternalLink, InfoIcon, RotateCcw } from 'lucide-react';
import { useCollapsibleSection } from 'shared/hooks/collapsibleSections';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import {
  useGetMondayBoardColumnsQuery,
  useGetMondaySupportQuery,
  useUpdateMondaySupportMutation,
} from '../services/projects.api';
import type { MondaySupportConfig } from '../types/integrations';
import MondaySupportPrefillSection from './monday-support/MondaySupportPrefillSection';
import MondaySupportVisibleColumnsSection from './monday-support/MondaySupportVisibleColumnsSection';

interface Props {
  projectId: number;
  projectName?: string;
}

const DEFAULT_BOARD_ID = '5789376087';

const DEFAULT_SUPPORT: MondaySupportConfig = {
  enabled: false,
  boardId: DEFAULT_BOARD_ID,
  formUrl: 'https://forms.monday.com/forms/embed/605e993de8d36c928738b9ce90d684f3',
  companyColumnId: 'text_mm4eynt',
  companyName: '',
  emailColumnId: 'texto_mkn3q6qm',
  projectIdColumnId: 'text_mm4en2hs',
  visibleColumnIds: ['name', 'status', 'creation_log', 'text_mm1c6k56', 'texto_mkn3q6qm', 'formula__1'],
};

const MondaySupportSection = ({ projectId, projectName }: Props) => {
  const sectionProps = useCollapsibleSection('monday-support');
  const { data, isLoading } = useGetMondaySupportQuery(projectId);
  const [updateMondaySupport, { isLoading: isSaving }] = useUpdateMondaySupportMutation();

  const [form, setForm] = useState<MondaySupportConfig>(DEFAULT_SUPPORT);
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const boardId = form.boardId?.trim() || undefined;

  const { data: columns = [], isFetching: isLoadingColumns } = useGetMondayBoardColumnsQuery(
    { projectId, boardId },
    { skip: !boardId }
  );

  useEffect(() => {
    if (data) {
      const boardId = data.boardId === '' ? DEFAULT_BOARD_ID : data.boardId ?? '';
      setForm({
        ...DEFAULT_SUPPORT,
        ...data,
        boardId,
        enabled: data.enabled && !!boardId,
        companyName: data.companyName || projectName || '',
        visibleColumnIds: data.visibleColumnIds ?? DEFAULT_SUPPORT.visibleColumnIds,
      });
    }
  }, [data, projectName]);

  const set = (field: keyof MondaySupportConfig, value: unknown) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (!next.boardId) next.enabled = false;
      return next;
    });
  };

  const handleCancel = () => {
    const boardId = data?.boardId === '' ? DEFAULT_BOARD_ID : data?.boardId ?? '';
    setForm({
      ...DEFAULT_SUPPORT,
      ...data,
      boardId,
      enabled: (data?.enabled ?? false) && !!boardId,
      companyName: data?.companyName || projectName || '',
      visibleColumnIds: data?.visibleColumnIds ?? DEFAULT_SUPPORT.visibleColumnIds,
    });
    setSuccessMessage(null);
    setErrorMessage(null);
    setIsEditing(false);
  };

  const handleReset = () => {
    setForm({ ...DEFAULT_SUPPORT, companyName: projectName || '' });
  };

  const handleSave = async () => {
    setSuccessMessage(null);
    setErrorMessage(null);
    try {
      await updateMondaySupport({ projectId, support: form }).unwrap();
      setSuccessMessage('Configuración guardada.');
      setIsEditing(false);
    } catch (e: unknown) {
      const message =
        e && typeof e === 'object' && 'data' in e && e.data && typeof e.data === 'object' && 'message' in e.data
          ? String((e.data as { message: string }).message)
          : 'Error al guardar. Intenta de nuevo.';
      setErrorMessage(message);
    }
  };

  return (
    <Accordion
      type="single"
      collapsible
      {...sectionProps}
      className="bg-bg-surface border border-border-subtle rounded-xl"
    >
      <AccordionItem value="content" className="border-none">
        <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-bg-surface-raised rounded-xl data-[state=open]:rounded-b-none">
          <span className="flex items-center gap-3">
            <span className="font-semibold text-text-default">Monday Soporte</span>
            <Badge className={form.enabled ? 'bg-success-light text-success' : 'bg-neutral-100 text-text-subtle'}>
              {form.enabled ? 'Habilitado' : 'Deshabilitado'}
            </Badge>
          </span>
        </AccordionTrigger>
        <AccordionContent className="px-5 pb-5 border-t border-border-subtle">
          {isLoading ? (
            <div className="py-4 text-sm text-text-subtle">Cargando...</div>
          ) : (
            <div className="flex flex-col gap-4 pt-4">
              <div className="flex items-center gap-3">
                <Switch
                  id="support-enabled"
                  checked={form.enabled}
                  onCheckedChange={(checked) => set('enabled', checked)}
                  disabled={!isEditing}
                />
                <Label htmlFor="support-enabled" className="text-sm font-medium cursor-pointer">
                  Activar soporte Monday
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <InfoIcon className="size-3.5 text-text-subtle cursor-default shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      Controla la visibilidad del soporte Monday en el Partner Center
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {isEditing && (
                  <Button size="sm" variant="outline" onClick={handleReset}>
                    <RotateCcw className="size-3.5 mr-1" />
                    Restaurar valores por defecto
                  </Button>
                )}
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-3 sm:max-w-[50%] flex-1 min-w-0">
                    <Label className="w-20 shrink-0 text-xs font-semibold uppercase tracking-widest text-text-subtle">
                      Board ID
                    </Label>
                    <Input
                      type="text"
                      value={form.boardId ?? ''}
                      onChange={(e) => set('boardId', e.target.value)}
                      placeholder={DEFAULT_BOARD_ID}
                      className="normal-case font-normal min-w-0"
                      disabled={!isEditing}
                    />
                  </div>
                  {boardId && (
                    <Button size="sm" variant="default" className="bg-blue-600 hover:bg-blue-700 shrink-0" asChild>
                      <a
                        href={`https://lidz-ai.monday.com/boards/${boardId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="size-3.5" />
                      </a>
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-3 sm:max-w-[50%] flex-1 min-w-0">
                    <Label className="w-20 shrink-0 text-xs font-semibold uppercase tracking-widest text-text-subtle">
                      Form URL
                    </Label>
                    <Input
                      type="url"
                      value={form.formUrl ?? ''}
                      onChange={(e) => set('formUrl', e.target.value)}
                      placeholder="https://forms.monday.com/forms/..."
                      className="normal-case font-normal min-w-0"
                      disabled={!isEditing}
                    />
                  </div>
                  {form.formUrl && (
                    <Button size="sm" variant="default" className="bg-blue-600 hover:bg-blue-700 shrink-0" asChild>
                      <a href={form.formUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="size-3.5" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>

              {boardId && (
                <>
                  <MondaySupportPrefillSection
                    form={form}
                    columns={columns}
                    isLoadingColumns={isLoadingColumns}
                    disabled={!isEditing}
                    onChange={set}
                  />
                  <MondaySupportVisibleColumnsSection
                    value={form.visibleColumnIds ?? []}
                    columns={columns}
                    isLoadingColumns={isLoadingColumns}
                    disabled={!isEditing}
                    onChange={(v) => set('visibleColumnIds', v)}
                  />
                </>
              )}

              <div className="flex flex-col gap-1">
                {isEditing ? (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      disabled={isSaving}
                      onClick={() => {
                        void handleSave();
                      }}
                    >
                      {isSaving ? 'Guardando...' : 'Guardar'}
                    </Button>
                    <Button size="sm" variant="outline" disabled={isSaving} onClick={handleCancel}>
                      Cancelar
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" className="self-start" onClick={() => setIsEditing(true)}>
                    Editar
                  </Button>
                )}
                {successMessage && <p className="text-sm text-success">{successMessage}</p>}
                {errorMessage && <p className="text-sm text-error">{errorMessage}</p>}
              </div>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default MondaySupportSection;
